import { backend } from 'declarations/backend';

const ITEMS_PER_PAGE = 10;
let currentPage = 1;
let supplyChart = null;

// Format number with commas and 8 decimal places
function formatNumber(num) {
    return num.toLocaleString('en-US', {
        minimumFractionDigits: 8,
        maximumFractionDigits: 8
    });
}

// Format timestamp to local date and time
function formatTimestamp(timestamp) {
    return new Date(Number(timestamp) / 1000000).toLocaleString();
}

// Fetch current supply and update stats
async function fetchCurrentSupply() {
    try {
        const supply = await backend.getCurrentSupply();
        document.getElementById('currentSupply').textContent = formatNumber(supply);
        
        // Update supply changes
        const changes = await backend.getSupplyChanges();
        document.querySelector('#change24h .stat-value').textContent = 
            (changes.day >= 0 ? '+' : '') + formatNumber(changes.day);
        document.querySelector('#change1w .stat-value').textContent = 
            (changes.week >= 0 ? '+' : '') + formatNumber(changes.week);
        document.querySelector('#change1m .stat-value').textContent = 
            (changes.month >= 0 ? '+' : '') + formatNumber(changes.month);
        
        return supply;
    } catch (error) {
        console.error('Error fetching supply:', error);
        document.getElementById('currentSupply').textContent = 'Error loading supply';
        return 0;
    }
}

// Fetch all conversions
async function fetchConversions() {
    try {
        const conversions = await backend.getConversions();
        return conversions.sort((a, b) => Number(b.timestamp) - Number(a.timestamp));
    } catch (error) {
        console.error('Error fetching conversions:', error);
        return [];
    }
}

// Get supply history
async function fetchSupplyHistory() {
    try {
        return await backend.getSupplyHistory();
    } catch (error) {
        console.error('Error fetching supply history:', error);
        return [];
    }
}

// Get total number of transactions
async function getTotalTransactions() {
    const conversions = await fetchConversions();
    return conversions.length;
}

// Fetch transactions for current page
async function fetchTransactions(page) {
    const conversions = await fetchConversions();
    const start = (page - 1) * ITEMS_PER_PAGE;
    return conversions.slice(start, start + ITEMS_PER_PAGE);
}

// Update conversions table
async function updateConversions() {
    const tableBody = document.getElementById('conversionTable');
    const totalTransactions = await getTotalTransactions();
    const transactions = await fetchTransactions(currentPage);

    document.getElementById('totalTransactions').textContent = totalTransactions;
    const startRange = (currentPage - 1) * ITEMS_PER_PAGE + 1;
    const endRange = Math.min(currentPage * ITEMS_PER_PAGE, totalTransactions);
    document.getElementById('currentRange').textContent = `${startRange}-${endRange}`;

    document.getElementById('prevPage').classList.toggle('disabled', currentPage == 1);
    document.getElementById('nextPage').classList.toggle('disabled', endRange >= totalTransactions);

    tableBody.innerHTML = transactions.length ? '' : '<tr><td colspan="4" class="text-center">No transactions found</td></tr>';

    transactions.forEach(tx => {
        const row = document.createElement('tr');
        row.innerHTML = `
            <td>
                <a href="https://dashboard.internetcomputer.org/bitcoin/transaction/${tx.id}" 
                   class="transaction-link" target="_blank">
                    ${tx.id}
                </a>
            </td>
            <td>${formatTimestamp(tx.timestamp)}</td>
            <td>${formatNumber(tx.fromAmount)} ckBTC</td>
            <td>
                <span class="badge ${tx.conversionType == 'mint' ? 'bg-success' : 'bg-danger'}">
                    ${tx.conversionType}
                </span>
            </td>
        `;
        tableBody.appendChild(row);
    });
}

// Initialize and update chart
async function updateChart(timeRange = 'all') {
    const supplyHistory = await fetchSupplyHistory();
    const ctx = document.getElementById('supplyChart').getContext('2d');
    
    if (supplyChart) {
        supplyChart.destroy();
    }

    const chartData = processChartData(supplyHistory, timeRange);
    
    const config = {
        type: 'line',
        data: {
            labels: chartData.labels,
            datasets: [{
                label: 'ckBTC Supply',
                data: chartData.data,
                borderColor: '#3949ab',
                tension: 0.4,
                fill: false
            }]
        },
        options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                legend: {
                    display: false
                }
            },
            scales: {
                y: {
                    beginAtZero: false,
                    ticks: {
                        callback: function(value) {
                            return value.toFixed(8);
                        }
                    }
                }
            },
            animation: false
        }
    };
    
    supplyChart = new Chart(ctx, config);
}

// Process chart data based on time range
function processChartData(supplyHistory, timeRange) {
    const now = Date.now();
    const timeRanges = {
        '1d': 24 * 60 * 60 * 1000,
        '7d': 7 * 24 * 60 * 60 * 1000,
        '1m': 30 * 24 * 60 * 60 * 1000,
        '3m': 90 * 24 * 60 * 60 * 1000,
        '1y': 365 * 24 * 60 * 60 * 1000,
        'all': Infinity
    };

    const filteredData = supplyHistory.filter(([timestamp, _]) => {
        const txTime = Number(timestamp) / 1000000;
        return (now - txTime) <= timeRanges[timeRange];
    });

    return {
        labels: filteredData.map(([timestamp, _]) => 
            new Date(Number(timestamp) / 1000000).toLocaleDateString()
        ),
        data: filteredData.map(([_, value]) => value)
    };
}

// Initialize the dashboard
async function initializeDashboard() {
    await fetchCurrentSupply();
    await updateConversions();
    await updateChart();

    // Set up periodic updates
    setInterval(async () => {
        await fetchCurrentSupply();
        await updateConversions();
        await updateChart(
            document.querySelector('.time-range-selector .btn.active').dataset.range
        );
    }, 300000); // Update every 5 minutes

    // Event listeners
    document.getElementById('prevPage').addEventListener('click', (e) => {
        e.preventDefault();
        if (currentPage > 1) {
            currentPage--;
            updateConversions();
        }
    });

    document.getElementById('nextPage').addEventListener('click', (e) => {
        e.preventDefault();
        getTotalTransactions().then(total => {
            if (currentPage * ITEMS_PER_PAGE < total) {
                currentPage++;
                updateConversions();
            }
        });
    });

    document.querySelectorAll('.time-range-selector .btn').forEach(btn => {
        btn.addEventListener('click', () => {
            document.querySelector('.time-range-selector .btn.active').classList.remove('active');
            btn.classList.add('active');
            updateChart(btn.dataset.range);
        });
    });
}

// Start the application
initializeDashboard();
